import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import BASE_URL from "../config";

const PublicNavbar = () => {
    const [menuItems, setMenuItems] = useState([]);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false); // For mobile menu
    const location = useLocation();

    useEffect(() => {
        const token = localStorage.getItem("token");
        setIsLoggedIn(!!token); // Set isLoggedIn to true if token exists
    }, []);

    const handleLogout = () => {
        localStorage.removeItem("token"); // Remove token from localStorage
        setIsLoggedIn(false); // Update state
        window.location.href = "/"; // Redirect to home page
    };

    useEffect(() => {
        const fetchMenuItems = async () => {
            try {
                const response = await fetch(`${BASE_URL}api/pages/find-active`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const data = await response.json();

                // Sort menuItems: "Home" first, "Contact Us" last
                const sortedMenuItems = data.sort((a, b) => {
                    if (a.pagename === "Home") return -1;
                    if (b.pagename === "Home") return 1;
                    if (a.pagename === "Contact Us") return 1;
                    if (b.pagename === "Contact Us") return -1;
                    return 0;
                });

                setMenuItems(sortedMenuItems);
            } catch (error) {
                console.error("Error fetching menu items:", error);
            }
        };

        fetchMenuItems();
    }, []);

    // Close menu on route change (mobile)
    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    return (
        <div
            style={{
                borderBottom: "1px solid #ddd",
                backgroundColor: "#ffffff", 
            }}
        >
            <style>
                {`
                @media (max-width: 992px) {
                    .public-navbar-inner {
                        height: 60px !important;
                        width: 100% !important;
                        padding: 0 10px !important;
                    }
                    .public-navbar-logo img {
                        height: 45px !important;
                    }
                    .public-navbar-menu {
                        display: none !important;
                        position: absolute;
                        top: 60px;
                        left: 0;
                        width: 100vw;
                        background: #fff;
                        flex-direction: column;
                        gap: 0;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.08);
                        z-index: 100;
                    }
                    .public-navbar-menu.open {
                        display: flex !important;
                    }
                    .public-navbar-link {
                        padding: 16px 20px;
                        border-bottom: 1px solid #eee;
                        width: 100%;
                        text-align: left;
                    }
                    .public-navbar-hamburger {
                        display: flex !important;
                    }
                }
                @media (max-width: 576px) {
                    .public-navbar-inner {
                        height: 50px !important;
                    }
                    .public-navbar-logo img {
                        height: 32px !important;
                    }
                }
                .public-navbar-hamburger {
                    display: none;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    width: 40px;
                    height: 40px;
                    cursor: pointer;
                    margin-left: 10px;
                }
                .public-navbar-hamburger span {
                    display: block;
                    width: 28px;
                    height: 3px;
                    background: #333;
                    margin: 4px 0;
                    border-radius: 2px;
                    transition: all 0.3s;
                }
                `}
            </style>
            <div
                className="top-bar d-flex justify-content-between align-items-center px-3 py-2 bg-white public-navbar-inner"
                style={{
                    fontFamily: "'Lato', Helvetica, Arial, Lucida, sans-serif",
                    height: "80px",
                    width: "80%",
                    maxWidth: "1080px",
                    margin: "0 auto",
                    position: "relative",
                }}
            >

                <div className="left public-navbar-logo">
                    <Link to="/">
                        <img
                            src="/nexorts-logo.png"
                            alt="Nexorts Logo"
                            style={{ height: "75px" }}
                        />
                    </Link>
                </div>

                {/* Hamburger icon for mobile */}
                <div
                    className="public-navbar-hamburger"
                    onClick={() => setMenuOpen((open) => !open)}
                    aria-label="Toggle menu"
                    tabIndex={0}
                    role="button"
                >
                    <span style={{ transform: menuOpen ? "rotate(45deg) translateY(8px)" : "none" }} />
                    <span style={{ opacity: menuOpen ? 0 : 1 }} />
                    <span style={{ transform: menuOpen ? "rotate(-45deg) translateY(-8px)" : "none" }} />
                </div>

                {/* Only show menu items on large screens or when toggled on small screens */}
                <div
                    className={`right d-flex align-items-center public-navbar-menu${menuOpen ? " open" : ""}`}
                    style={{
                        // On large screens, always flex. On small screens, only flex if open.
                        display:
                            window.innerWidth > 992
                                ? "flex"
                                : menuOpen
                                ? "flex"
                                : "none",
                        gap: "10px",
                        position: menuOpen ? "absolute" : "static",
                        right: 0,
                        top: menuOpen ? "60px" : "auto",
                        background: menuOpen ? "#fff" : "none",
                        flexDirection: menuOpen ? "column" : "row",
                        width: menuOpen ? "100vw" : "auto",
                        boxShadow: menuOpen ? "0 2px 8px rgba(0,0,0,0.08)" : "none",
                        zIndex: menuOpen ? 100 : "auto",
                        padding: menuOpen ? "0" : "",
                    }}
                >
                    {/* Always show menu items, even on login/register */}
                    {menuItems.map((item) => {
                        const isHome = item.pagename === "Home";
                        const isActive = (isHome && location.pathname === "/") || location.pathname === `/${item.slug}`;
                        return (
                            <Link
                                key={item.slug}
                                to={`/${item.slug}`}
                                className={
                                    "btn text-dark text-decoration-none public-navbar-link" +
                                    (isActive ? " active-public-navbar-link" : "")
                                }
                                style={{
                                    outline: "none",
                                    color: isActive ? "#2ea3f2" : undefined,
                                    fontWeight: isActive ? "bold" : undefined,
                                    ...(isActive && { WebkitTextFillColor: "#22b98e" }),
                                    ...(isActive && { "--active-color": "#22b98e" }),
                                }}
                            >
                                {item.pagename}
                            </Link>
                        );
                    })}
                    {isLoggedIn ? (
                        <>
                            <Link
                                to="/dashboard"
                                className="btn text-dark text-decoration-none public-navbar-link"
                                style={{ outline: "none" }}
                            >
                                Dashboard
                            </Link>
                            <button
                                onClick={handleLogout}
                                className="btn text-dark text-decoration-none public-navbar-link"
                                style={{ outline: "none", cursor: "pointer" }}
                            >
                                Logout
                            </button>
                        </>
                    ) : (
                        <Link
                            to="/login"
                            className={
                                "btn text-dark text-decoration-none public-navbar-link" +
                                (location.pathname === "/login" ? " active-public-navbar-link" : "")
                            }
                            style={{
                                outline: "none",
                                color: location.pathname === "/login" ? "#2ea3f2" : undefined,
                                fontWeight: location.pathname === "/login" ? "bold" : undefined,
                                ...(location.pathname === "/login" && { WebkitTextFillColor: "#2ea3f2" }),
                                ...(location.pathname === "/login" && { "--active-color": "#2ea3f2" }),
                            }}
                        >
                            Login
                        </Link>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PublicNavbar;
